// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Is a value "empty" for `empty` / `not_empty`?
 *
 * Enumerated rather than relying on falsiness, because `0` and `false` are
 * answers. A number of zero records affected is a fact, not a blank.
 */
export function isEmptyAnswer(value) {
    if (value === null || value === undefined)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
/** Normalise an answer to the string set used for comparison. */
function answerValues(value) {
    if (value === null || value === undefined)
        return [];
    if (Array.isArray(value))
        return value.filter((v) => v !== null && v !== undefined).map(String);
    return [String(value)];
}
/** Normalise a rule's `value` to a list, so `in` accepts a string or an array. */
function ruleValues(value) {
    if (value === null || value === undefined)
        return [];
    return Array.isArray(value) ? value.map(String) : [String(value)];
}
/**
 * Does one rule's condition hold against the current answers?
 *
 * Multi-value answers (a multiselect) satisfy `equals` when **any** selected
 * value matches. That is what makes gate 2 expressible: `legal_basis` becomes a
 * multiselect of the six Art. 6(1) bases, and the LIA reference is required
 * when legitimate interests is *among* the selected bases — not only when it is
 * the sole basis, which would be the wrong reading of Art. 6.
 */
export function evaluateCondition(rule, answers) {
    // A missing answer map is the same as no answers, not a crash. Both current
    // call sites guard it (`instance.field_values ?? {}` on the server, always an
    // object in the renderer) — but this is shared infrastructure listed in
    // REUSE.md, and a future caller has no reason to know that. Fail closed:
    // no answers means no condition is met, which demands nothing extra.
    const answer = answers ? answers[rule.source_field_id] : undefined;
    const answerSet = answerValues(answer);
    const expected = ruleValues(rule.value);
    switch (rule.operator) {
        case 'equals':
            return expected.length > 0 && answerSet.some((a) => expected.includes(a));
        case 'not_equals':
            return expected.length > 0 && !answerSet.some((a) => expected.includes(a));
        case 'contains':
            // Substring containment on the rendered answer — the operator's meaning
            // for free text. For an array answer, any element containing the needle.
            return expected.length > 0 && answerSet.some((a) => expected.some((e) => a.includes(e)));
        case 'not_empty':
            return !isEmptyAnswer(answer);
        case 'empty':
            return isEmptyAnswer(answer);
        case 'in':
            return answerSet.length > 0 && answerSet.every((a) => expected.includes(a));
        case 'not_in':
            return answerSet.length > 0 && !answerSet.some((a) => expected.includes(a));
        default:
            // An operator the evaluator does not implement must not silently pass.
            // Pack validation rejects these, so reaching here means a rule bypassed
            // validation — fail closed rather than apply an unknown condition.
            return false;
    }
}
/**
 * Evaluate a rule set into per-field state.
 *
 * ## Precedence (AC4), and why
 *
 * Rules are declarative and several may target one field, so the outcome must
 * not depend on authoring order. The resolution is:
 *
 * 1. **`hide` beats `show`.** A field any rule hides is hidden, whatever else
 *    says. The alternative — last-rule-wins — makes the result depend on array
 *    order in JSON, which no author can reason about.
 * 2. **A hidden field is never required.** This is the rule that keeps the form
 *    submittable. Without it, one rule hiding a field and another requiring it
 *    produces a form that cannot be completed and gives the user nothing to
 *    act on — the failure mode that makes conditional forms unusable.
 * 3. **`disable` is independent of visibility.** A disabled visible field is a
 *    legitimate read-only state; a disabled hidden field is simply hidden.
 *
 * Defaults come from the field's authored state, passed in by the caller, so a
 * form with no rules evaluates to exactly what the pack declares.
 */
export function evaluateConditionalLogic(rules, answers, defaults = {}) {
    const state = {};
    const ensure = (fieldId) => {
        if (!state[fieldId]) {
            const d = defaults[fieldId] ?? {};
            state[fieldId] = {
                visible: d.visible ?? true,
                required: d.required ?? false,
                disabled: d.disabled ?? false,
            };
        }
        return state[fieldId];
    };
    for (const fieldId of Object.keys(defaults))
        ensure(fieldId);
    if (!Array.isArray(rules) || rules.length === 0)
        return state;
    // Collected first, applied after, so `hide` cannot depend on rule order.
    const hidden = new Set();
    const shown = new Set();
    const required = new Set();
    const disabled = new Set();
    for (const rule of rules) {
        if (!evaluateCondition(rule, answers))
            continue;
        for (const target of rule.target_field_ids ?? []) {
            ensure(target);
            switch (rule.action) {
                case 'hide':
                    hidden.add(target);
                    break;
                case 'show':
                    shown.add(target);
                    break;
                case 'require':
                    required.add(target);
                    break;
                case 'disable':
                    disabled.add(target);
                    break;
            }
        }
    }
    for (const fieldId of shown)
        ensure(fieldId).visible = true;
    for (const fieldId of hidden)
        ensure(fieldId).visible = false; // (1) hide beats show
    for (const fieldId of disabled)
        ensure(fieldId).disabled = true;
    for (const fieldId of required)
        ensure(fieldId).required = true;
    // (2) a hidden field is never required — applied last so it cannot be undone
    for (const fieldId of Object.keys(state)) {
        if (!state[fieldId].visible)
            state[fieldId].required = false;
    }
    return state;
}
/**
 * Fields a rule set can reach, for validation and for memoisation keys.
 * Includes both sources and targets: a rule is unresolvable if either is absent.
 */
export function referencedFieldIds(rules) {
    const sources = new Set();
    const targets = new Set();
    for (const rule of rules ?? []) {
        if (rule.source_field_id)
            sources.add(rule.source_field_id);
        for (const t of rule.target_field_ids ?? [])
            targets.add(t);
    }
    return { sources: [...sources], targets: [...targets] };
}
/**
 * Detect a cycle in the rule graph (AC6).
 *
 * An edge runs source → target: the answer to one field decides another's
 * state. A cycle means a field's visibility depends, transitively, on itself —
 * unresolvable at render time and undebuggable from the UI, so it is rejected
 * at validation time instead.
 *
 * Returns the field ids forming the first cycle found, or null.
 */
export function findConditionalCycle(rules) {
    const edges = new Map();
    for (const rule of rules ?? []) {
        if (!rule.source_field_id)
            continue;
        const set = edges.get(rule.source_field_id) ?? new Set();
        for (const t of rule.target_field_ids ?? [])
            set.add(t);
        edges.set(rule.source_field_id, set);
    }
    const VISITING = 1;
    const DONE = 2;
    const marks = new Map();
    const stack = [];
    const walk = (node) => {
        const mark = marks.get(node);
        if (mark === DONE)
            return null;
        if (mark === VISITING) {
            const from = stack.indexOf(node);
            return [...stack.slice(from), node];
        }
        marks.set(node, VISITING);
        stack.push(node);
        for (const next of edges.get(node) ?? []) {
            const cycle = walk(next);
            if (cycle)
                return cycle;
        }
        stack.pop();
        marks.set(node, DONE);
        return null;
    };
    for (const node of edges.keys()) {
        const cycle = walk(node);
        if (cycle)
            return cycle;
    }
    return null;
}
//# sourceMappingURL=conditional-logic.js.map